/**
 * @fileoverview Parser for inline interactive components in chat messages.
 * Converts [[componentId]] syntax and [[type:param1:param2:...]] syntax into renderable React components.
 * Supports buttons, links, badges, code snippets, and other interactive elements.
 *
 * @module utils/chat/inlineComponentParser
 */

/**
 * Parses parameters from inline component syntax, handling escaped colons and URLs.
 * @param {string} paramString - The parameter string (e.g., "text:href:true")
 * @param {string} componentType - The type of component for smart parsing
 * @returns {string[]} Array of unescaped parameters
 */
function parseParameters(paramString, componentType) {
  // For URLs, we need special handling to avoid breaking on protocol colons
  if (componentType === 'link') {
    return parseLinkParameters(paramString)
  }
  // Image src is also a URL that needs special handling
  if (componentType === 'image') {
    return parseImageParameters(paramString)
  }

  const params = []
  let current = ''
  let i = 0

  while (i < paramString.length) {
    if (paramString[i] === '\\' && paramString[i + 1] === ':') {
      // Escaped colon - add literal colon and skip escape char
      current += ':'
      i += 2
    } else if (paramString[i] === ':') {
      // Unescaped colon - parameter boundary
      params.push(current)
      current = ''
      i++
    } else {
      // Regular character
      current += paramString[i]
      i++
    }
  }

  // Add the last parameter
  if (current || params.length > 0) {
    params.push(current)
  }

  return params
}

/**
 * Smart parsing for image parameters that handles URLs with colons.
 * Format: src:alt:caption
 * @param {string} paramString - The parameter string
 * @returns {string[]} Array of parsed parameters
 */
function parseImageParameters(paramString) {
  const parts = []
  let current = ''
  let i = 0
  let paramCount = 0

  while (i < paramString.length) {
    if (paramString[i] === '\\' && paramString[i + 1] === ':') {
      // Escaped colon - add literal colon
      current += ':'
      i += 2
    } else if (paramString[i] === ':') {
      // Check if this looks like a protocol (://), if so keep it
      if (paramCount === 0 && paramString.substring(i, i + 3) === '://') {
        // This is likely a protocol in a URL, keep it
        current += paramString[i]
        i++
      } else {
        // Regular parameter boundary
        parts.push(current)
        current = ''
        paramCount++
        i++
      }
    } else {
      current += paramString[i]
      i++
    }
  }

  // Add the last parameter
  if (current || parts.length > 0) {
    parts.push(current)
  }

  return parts
}

/**
 * Smart parsing for link parameters that handles URLs with colons.
 * Format: text:href:external
 * @param {string} paramString - The parameter string
 * @returns {string[]} Array of parsed parameters
 */
function parseLinkParameters(paramString) {
  const parts = []
  let current = ''
  let i = 0
  let paramCount = 0

  while (i < paramString.length) {
    if (paramString[i] === '\\' && paramString[i + 1] === ':') {
      // Escaped colon - add literal colon
      current += ':'
      i += 2
    } else if (paramString[i] === ':') {
      // Check if this looks like a protocol (://), if so keep it
      if (paramCount === 1 && paramString.substring(i, i + 3) === '://') {
        // This is likely a protocol in a URL, keep it
        current += paramString[i]
        i++
      } else {
        // Regular parameter boundary
        parts.push(current)
        current = ''
        paramCount++
        i++
      }
    } else {
      current += paramString[i]
      i++
    }
  }

  // Add the last parameter
  if (current || parts.length > 0) {
    parts.push(current)
  }

  return parts
}

/**
 * Component factories for creating component objects from inline data.
 * Each factory converts parameters into component definitions.
 */
const componentFactories = {
  badge: (params) => ({
    type: 'badge',
    text: params[0] || 'Badge',
    color: params[1] || 'gray',
  }),
  button: (params) => ({
    type: 'button',
    text: params[0] || 'Button',
    action: params[1] || 'default',
    variant: params[2] || 'secondary',
    disabled: params[3] === 'true',
  }),
  link: (params) => ({
    type: 'link',
    text: params[0] || 'Link',
    href: params[1] || '#',
    external: params[2] === 'true',
  }),
  code: (params) => ({
    type: 'code',
    text: params[0] || '',
    copyable: params[1] === 'true',
  }),
  heading: (params) => ({
    type: 'heading',
    text: params[0] || 'Heading',
    level: parseInt(params[1]) || 2,
  }),
  image: (params) => ({
    type: 'image',
    src: params[0] || '',
    alt: params[1] || 'Image',
    caption: params[2] || '',
  }),
}

/**
 * Parses text containing inline component references and returns an array of parts.
 * Components are referenced using [[componentId]] syntax or [[type:param1:param2:...]] syntax
 * and replaced with their corresponding component definitions.
 *
 * @param {string} text - The text to parse, may contain [[componentId]] or [[type:params]] references
 * @param {Object} components - Map of component IDs to component definitions
 * @param {string} components[].type - Component type (e.g., 'button', 'link', 'badge')
 * @param {Object} components[].props - Component-specific properties
 *
 * @returns {Array<Object>} Array of parsed parts
 * @returns {string} returns[].type - Part type ('text' or 'component')
 * @returns {string} [returns[].content] - Text content (for text parts)
 * @returns {string} [returns[].componentType] - Component type (for component parts)
 * @returns {Object} [returns[].props] - Component properties (for component parts)
 *
 * @example
 * // Existing format
 * const text = "Click [[button1]] to continue or visit [[link1]]"
 * const components = {
 *   button1: { type: 'button', label: 'Next', action: 'next' },
 *   link1: { type: 'link', href: 'https://example.com', label: 'Example' }
 * }
 *
 * // New inline format
 * const text = "Status: [[badge:Active:green]] Click [[button:Continue:next:primary]]"
 *
 * const parts = parseInlineComponents(text, components)
 * // Returns parsed components for both formats
 */
export function parseInlineComponents(text, components = {}) {
  if (!text || typeof text !== 'string') {
    return [{ type: 'text', content: text || '' }]
  }

  // Pre-process inline data format [[type:param1:param2:...]] into existing format
  const INLINE_DATA_PATTERN = /\[\[([a-zA-Z]+):((?:[^\]]|\][^\]])*?)\]\]/g
  let processedText = text
  const inlineComponents = { ...components }
  let inlineCounter = 0

  // Transform inline format to existing format
  processedText = processedText.replace(INLINE_DATA_PATTERN, (match, type, params) => {
    const factory = componentFactories[type]
    if (factory) {
      const paramArray = parseParameters(params, type)
      const component = factory(paramArray)
      const inlineId = `_inline_${inlineCounter++}`
      inlineComponents[inlineId] = component
      return `[[${inlineId}]]`
    }
    return match // Keep original if no factory found
  })

  // Existing logic for [[componentId]] patterns
  const pattern = /\[\[(\w+)\]\]/g
  const parts = []
  let lastIndex = 0
  let match

  while ((match = pattern.exec(processedText)) !== null) {
    // Add text before the component
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: processedText.slice(lastIndex, match.index),
      })
    }

    // Add the component if it exists
    const componentId = match[1]
    const component = inlineComponents[componentId]

    if (component) {
      parts.push({
        type: 'component',
        componentType: component.type,
        props: component,
      })
    } else {
      // If component not found, keep original text
      parts.push({
        type: 'text',
        content: match[0],
      })
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < processedText.length) {
    parts.push({
      type: 'text',
      content: processedText.slice(lastIndex),
    })
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: processedText }]
}
